import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

// ==================== TAFSIR API ====================

/**
 * Get tafsir for a specific verse
 * @param {number} chapterId - Surah number
 * @param {number} verseNumber - Ayah number
 * @param {string} tafsirType - 'ibnu_katsir' or 'jalalain'
 * @returns {Promise<Object>}
 */
export const getTafsirByAyah = async (chapterId, verseNumber, tafsirType = 'ibnu_katsir') => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/api/tafsir/verses/${chapterId}:${verseNumber}`,
      { params: { tafsir_type: tafsirType } }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching tafsir:', error);
    throw error;
  }
};

/**
 * Get all tafsir types for a specific verse
 * @param {number} chapterId - Surah number
 * @param {number} verseNumber - Ayah number
 * @returns {Promise<Object>}
 */
export const getAllTafsirByAyah = async (chapterId, verseNumber) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/api/tafsir/verses/${chapterId}:${verseNumber}/all`
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching all tafsirs:', error);
    throw error;
  }
};

/**
 * Get all tafsir for a specific chapter
 * @param {number} chapterId - Surah number
 * @returns {Promise<Object>}
 */
export const getTafsirByChapter = async (chapterId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/api/tafsir/chapters/${chapterId}`
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching chapter tafsir:', error);
    throw error;
  }
};

// ==================== WORD BY WORD API ====================

/**
 * Get word-by-word analysis for a specific verse
 * @param {number} chapterId - Surah number
 * @param {number} verseNumber - Ayah number
 * @returns {Promise<Object>}
 */
export const getWordByWord = async (chapterId, verseNumber) => {
  const build = async (lang) => {
    const url = `https://api.quran.com/api/v4/verses/by_key/${chapterId}:${verseNumber}`;
    const res = await axios.get(url, {
      params: {
        words: true,
        language: lang,
        word_fields: "position,text_uthmani,translation,transliteration,char_type",
      },
    });
    const words = res.data?.verse?.words || res.data?.words || [];
    return words.filter((w) => (w.char_type_name || w.char_type) === "word");
  };
  try {
    const [idWords, enWords] = await Promise.allSettled([build("id"), build("en")]);
    const idArr = idWords.status === "fulfilled" ? idWords.value : [];
    const enArr = enWords.status === "fulfilled" ? enWords.value : [];
    const maxLen = Math.max(idArr.length, enArr.length);
    const merged = [];
    for (let i = 0; i < maxLen; i += 1) {
      const iw = idArr[i] || {};
      const ew = enArr[i] || {};
      merged.push({
        word_id: iw.id || ew.id || i + 1,
        word_position: iw.position || ew.position || i + 1,
        text_arabic: iw.text_uthmani || ew.text_uthmani || "",
        transliteration: iw.transliteration || ew.transliteration || "",
        translation_indonesian: iw.translation?.text || iw.translation || "",
        translation_english: ew.translation?.text || ew.translation || "",
        root_letters: null,
        part_of_speech: null,
      });
    }
    return {
      available: merged.length > 0,
      words: merged,
    };
  } catch {
    return { available: false, words: [] };
  }
};

// ==================== ASBABUN NUZUL API ====================

/**
 * Get asbabun nuzul for a specific chapter (surah-level)
 * @param {number} chapterId - Surah number
 * @returns {Promise<Object>}
 */
export const getAsbabunNuzulChapter = async (chapterId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/api/asbabun-nuzul/chapters/${chapterId}`
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching asbabun nuzul chapter:', error);
    throw error;
  }
};

/**
 * Get asbabun nuzul for a specific verse
 * @param {number} chapterId - Surah number
 * @param {number} verseNumber - Ayah number
 * @returns {Promise<Object>}
 */
export const getAsbabunNuzulAyah = async (chapterId, verseNumber) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/api/asbabun-nuzul/verses/${chapterId}:${verseNumber}`
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching asbabun nuzul ayah:', error);
    throw error;
  }
};

// ==================== HELPER FUNCTIONS ====================

/**
 * Format tafsir type name for display
 * @param {string} tafsirType - 'ibnu_katsir' or 'jalalain'
 * @returns {string}
 */
export const formatTafsirTypeName = (tafsirType) => {
  const names = {
    'ibnu_katsir': 'Tafsir Ibnu Katsir',
    'jalalain': 'Tafsir Jalalain'
  };
  return names[tafsirType] || tafsirType;
};

/**
 * Check if tafsir is available
 * @param {Object} tafsir - Tafsir object
 * @returns {boolean}
 */
export const isTafsirAvailable = (tafsir) => {
  return tafsir && tafsir.available !== false && tafsir.text_indonesian;
};
